// Investigation for manual enrolment

/**
 * WebAuthn ceremony helpers for passkey enrollment.
 *
 * Auth0's My Account API speaks base64url-encoded strings; the browser's
 * WebAuthn API speaks `ArrayBuffer`. Everything in this file exists to bridge
 * those two representations and to run `navigator.credentials.create()`.
 */

/** Thrown when the authenticator refuses to create a duplicate credential. */
export const ALREADY_ENROLLED = "already_enrolled";

/** Thrown when the user dismissed the browser's native dialog. */
export const USER_CANCELLED = "user_cancelled";

/** Thrown when this device cannot do platform passkeys at all. */
export const UNSUPPORTED = "unsupported";

export class PasskeyCeremonyError extends Error {
    code: string;

    constructor(code: string, message?: string) {
        super(message || code);
        this.code = code;
        // Required when targeting ES5: without it, `instanceof` fails for
        // classes extending built-ins such as Error.
        Object.setPrototypeOf(this, PasskeyCeremonyError.prototype);
    }
}

/** base64url -> ArrayBuffer. */
function base64UrlToBuffer(value: string): ArrayBuffer {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const binary = window.atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

/** ArrayBuffer -> base64url (unpadded, as the WebAuthn/Auth0 wire format expects). */
function bufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/** True when the browser exposes the WebAuthn API at all. */
export function isWebAuthnAvailable(): boolean {
    return (
        typeof window !== "undefined" &&
        typeof window.PublicKeyCredential !== "undefined" &&
        typeof navigator !== "undefined" &&
        !!navigator.credentials &&
        typeof navigator.credentials.create === "function"
    );
}

/**
 * Reports whether this device has a built-in authenticator (Touch ID, Windows
 * Hello, Android screen lock).
 *
 * IMPORTANT: this only tells us the device is CAPABLE. It can never tell us
 * whether a passkey for our site already exists here - browsers deliberately
 * refuse to answer that, because enumerating credentials without user
 * interaction would be a fingerprinting leak. Knowing whether this device is
 * already enrolled is the job of `excludeCredentials` below.
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
    if (!isWebAuthnAvailable()) {
        return false;
    }

    const publicKeyCredential: any = window.PublicKeyCredential;
    if (typeof publicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== "function") {
        return false;
    }

    try {
        return await publicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
        // Some embedded browsers throw rather than resolving false.
        return false;
    }
}

/**
 * Converts Auth0's `authn_params_public_key` payload into the shape
 * `navigator.credentials.create()` expects.
 */
function toCreationOptions(authnParams: any): any {
    const options: any = { ...authnParams };

    options.challenge = base64UrlToBuffer(authnParams.challenge);

    if (authnParams.user) {
        options.user = { ...authnParams.user, id: base64UrlToBuffer(authnParams.user.id) };
    }

    // `excludeCredentials` is the single most important field here.
    //
    // Auth0 populates it with every passkey the ACCOUNT already has. Passing it
    // through UNMODIFIED is what makes per-device detection work: if this
    // authenticator already holds one of these credentials, it refuses to
    // create a duplicate and throws `InvalidStateError`. That rejection is the
    // only authoritative "this device is already enrolled" signal WebAuthn
    // gives us, and we translate it into a SUCCESS.
    //
    // Never filter or drop this list - doing so would let a user pile up
    // duplicate passkeys on one device and would break the detection entirely.
    if (Array.isArray(authnParams.excludeCredentials)) {
        options.excludeCredentials = authnParams.excludeCredentials.map((credential: any) => ({
            ...credential,
            id: base64UrlToBuffer(credential.id)
        }));
    }

    return options;
}

/**
 * Runs the credential creation ceremony and returns the payload Auth0's verify
 * endpoint expects.
 *
 * @throws {PasskeyCeremonyError} with code `already_enrolled`, `user_cancelled`
 *   or `unsupported`; any other failure is rethrown untouched.
 */
export async function createPasskeyCredential(authnParams: any): Promise<any> {
    if (!isWebAuthnAvailable()) {
        throw new PasskeyCeremonyError(UNSUPPORTED);
    }

    let credential: any;
    try {
        credential = await navigator.credentials.create({ publicKey: toCreationOptions(authnParams) } as any);
    } catch (error: any) {
        // The authenticator already holds one of the excluded credentials.
        // This is GOOD NEWS: the device is already enrolled, so the caller
        // should record success and stop prompting here.
        if (error && error.name === "InvalidStateError") {
            throw new PasskeyCeremonyError(ALREADY_ENROLLED);
        }

        // The user dismissed the dialog, or it timed out. Both are a decline.
        if (error && (error.name === "NotAllowedError" || error.name === "AbortError")) {
            throw new PasskeyCeremonyError(USER_CANCELLED);
        }

        if (error && error.name === "NotSupportedError") {
            throw new PasskeyCeremonyError(UNSUPPORTED);
        }

        // A `SecurityError` here is almost always tenant configuration rather
        // than anything wrong with this code or the user's device: the browser
        // enforces that `rp.id` must equal the page's origin or be a
        // registrable PARENT of it. Sibling hosts are rejected.
        //
        // Logged explicitly because the generic failure message the user sees
        // gives no clue which two values disagreed.
        if (error && error.name === "SecurityError") {
            console.error("[passkey] WebAuthn rejected the relying party ID", {
                relyingPartyId: authnParams?.rp?.id,
                pageOrigin: window.location.hostname,
                hint: "rp.id must equal the page host or be a registrable parent of it"
            });
        }

        throw error;
    }

    // Per spec `create()` resolves with null rather than rejecting in a few
    // edge cases, so this is not dead code.
    if (!credential) {
        throw new PasskeyCeremonyError(USER_CANCELLED);
    }

    const response = credential.response;

    return {
        id: credential.id,
        rawId: bufferToBase64Url(credential.rawId),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment,
        response: {
            clientDataJSON: bufferToBase64Url(response.clientDataJSON),
            attestationObject: bufferToBase64Url(response.attestationObject),
            transports:
                typeof response.getTransports === "function" ? response.getTransports() : undefined
        }
    };
}
